// src/components/common/Select.tsx

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { SelectOption } from '../../types';

interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    label?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    note?: string;
    warning?: string;
    name?: string;
}

const Select: React.FC<SelectProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Select an option...',
    label,
    error,
    required = false,
    disabled = false,
    className = '',
    note,
    warning,
    name,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={className} ref={dropdownRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            <div className="relative">
                <button
                    name={name}
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    className={`w-full px-4 py-2.5 border rounded-lg shadow-sm text-sm transition-colors flex items-center justify-between ${error
                        ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500'
                        } ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'}`}
                >
                    <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
                        {selectedOption?.label || placeholder}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && !disabled && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                    if (!option.disabled) {
                                        onChange(option.value);
                                        setIsOpen(false);
                                    }
                                }}
                                disabled={option.disabled}
                                className={`w-full px-4 py-2.5 text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${option.disabled
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'cursor-pointer'
                                    } ${value === option.value ? 'bg-blue-50' : ''}`}
                            >
                                <div className="flex flex-col items-start">
                                    <span className={option.disabled ? 'text-gray-400' : 'text-gray-700'}>
                                        {option.label}
                                    </span>
                                    {option.description && (
                                        <span className="text-xs text-gray-400 mt-0.5">
                                            {option.description}
                                        </span>
                                    )}
                                </div>
                                {value === option.value && !option.disabled && (
                                    <Check className="h-4 w-4 text-blue-600" />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
            {note && !error && !warning && (
                <p className="mt-1 text-xs text-gray-500">{note}</p>
            )}
            {warning && !error && (
                <p className="mt-1 text-xs text-amber-600">{warning}</p>
            )}
        </div>
    );
};

export default Select;